import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Printer, CheckCircle2, RotateCcw, ArrowDownLeft, ArrowUpRight, Search, Smartphone, ArrowLeft, ArrowRight, Handshake, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import adminApi from "@/lib/adminApi";
import { openContractWindow } from "@/pages/ContractActions";
import { ContractButton } from "@/pages/ContractActions";
import { openWarrantyWindow } from "@/components/WarrantyContractsTab";
import { ModelCombobox } from "@/components/ModelCombobox";
import { useAuth } from "@/context/AuthContext";

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(0)}`);
const modelSlug = (m) => String(m || "").replace(/\s+/g, "-").toLowerCase();
const genOf = (m) => { const x = String(m || "").match(/iPhone\s+(\d+)/i); return x ? Number(x[1]) : 0; };
const imeiShort = (imei) => (imei ? `IMEI …${String(imei).slice(-4)}` : null);
const EMPTY = { model: "", capacity: "", color: "", battery_percent: "", charge_cycles: "", condition: "", damage: "none", has_box: "no", sim_type: "", imei: "", customer: { name: "", phone: "", doc: "", email: "" } };

// Miniatura reutilizando la MISMA imagen del catálogo de Precios (por modelo).
const PhoneThumb = ({ model, color, className = "h-11 w-11" }) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  const [stage, setStage] = useState(0);
  const s = modelSlug(model);
  const src = stage === 0 ? `${base}/api/model-image/${encodeURIComponent(model)}${color ? `?color=${encodeURIComponent(color)}` : ""}` : stage === 1 ? `/phones/${s}.jpg` : null;
  return (
    <div className={`${className} shrink-0 rounded-lg border border-zinc-200 bg-white grid place-items-center overflow-hidden`}>
      {src ? <img src={src} alt={model} className="h-full w-full object-contain" onError={() => setStage((x) => x + 1)} /> : <Smartphone className="h-5 w-5 text-zinc-300" />}
    </div>
  );
};

// TRADE-IN MANUAL — wizard POS de 3 pasos. ENTRA equipo del cliente (motor de precios existente),
// SALE equipo DISPONIBLE (precio = Mercado Alto de Venta Simple). Operación atómica y contrato existentes.
export function ManualTradeInDialog({ open, onOpenChange, onCreated, prefill }) {
  const [step, setStep] = useState(1);
  const [confirming, setConfirming] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [catalog, setCatalog] = useState(null);
  const [avail, setAvail] = useState([]);
  const [newAvail, setNewAvail] = useState([]);
  const [outSource, setOutSource] = useState("used"); // "used" | "new"
  const [priceMap, setPriceMap] = useState({});
  const [outId, setOutId] = useState("");
  const [q, setQ] = useState("");
  const [filterGen, setFilterGen] = useState("all");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const { admin } = useAuth();
  const isAdmin = admin?.role === "admin";
  const [agreedValue, setAgreedValue] = useState("");
  const [opDate, setOpDate] = useState("");
  const [inboundCondition, setInboundCondition] = useState("used"); // "used" | "new" (equipo recibido)

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setC = (k, v) => setForm((f) => ({ ...f, customer: { ...f.customer, [k]: v } }));
  const selModel = catalog?.models?.find((m) => m.name === form.model) || null;
  const needsCycles = (selModel?.generation || 0) >= 15;
  const outActive = outSource === "used" ? avail : newAvail;
  const outbound = outActive.find((it) => it.id === outId) || null;
  const priceFor = (it) => (it ? (outSource === "used" ? priceMap[`${it.model}__${it.capacity}`] : (it.sale_price ?? null)) : null);

  useEffect(() => {
    if (!open) return;
    adminApi.get("/config").then((r) => setCatalog(r.data)).catch(() => {});
    adminApi.get("/admin/inventory", { params: { status: "DISPONIBLE" } }).then((r) => setAvail(r.data)).catch(() => toast.error("No se pudo cargar inventario disponible"));
    adminApi.get("/admin/new-iphones", { params: { status: "DISPONIBLE" } }).then((r) => setNewAvail(r.data)).catch(() => {});
    adminApi.get("/admin/price-reference").then((r) => { const m = {}; (r.data || []).forEach((row) => { m[`${row.model}__${row.capacity}`] = row.market_high; }); setPriceMap(m); }).catch(() => {});
  }, [open]);

  // Retiros → Trade-In: precarga desde una solicitud de retiro (equipo del consignador que sale,
  // cliente y datos de referencia del equipo recibido). Los datos de referencia NO son verificados.
  useEffect(() => {
    if (!open || !prefill) return;
    if (prefill.outbound) { setOutSource(prefill.outbound.kind || "new"); setOutId(prefill.outbound.id || ""); }
    setForm((f) => ({
      ...f,
      model: prefill.inbound?.model || f.model,
      capacity: prefill.inbound?.capacity || f.capacity,
      color: prefill.inbound?.color || f.color,
      battery_percent: prefill.inbound?.battery_percent != null ? String(prefill.inbound.battery_percent) : f.battery_percent,
      customer: { ...f.customer, name: prefill.customer?.name || f.customer.name, phone: prefill.customer?.phone || f.customer.phone },
    }));
    setStep(1);
    // eslint-disable-next-line
  }, [open, prefill]);

  const canPreview = form.model && form.capacity && form.battery_percent !== "" && form.condition && (!needsCycles || form.charge_cycles !== "");
  useEffect(() => {
    if (!open || !canPreview) { setPreview(null); return; }
    const body = {
      model: form.model, capacity: form.capacity, color: form.color || null,
      battery_percent: Number(form.battery_percent), problems: [form.damage || "none"],
      aesthetic: form.condition, has_box: form.has_box === "si", operation: "trade_in",
      charge_cycles: form.charge_cycles === "" ? null : Number(form.charge_cycles),
    };
    let cancel = false;
    adminApi.post("/admin/trade-in-quote-preview", body).then((r) => { if (!cancel) setPreview(r.data); }).catch(() => { if (!cancel) setPreview(null); });
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, [open, form.model, form.capacity, form.color, form.battery_percent, form.condition, form.damage, form.has_box, form.charge_cycles]);

  const close = (v) => { onOpenChange(v); if (!v) { setStep(1); setConfirming(false); setForm(EMPTY); setResult(null); setOutId(""); setQ(""); setFilterGen("all"); setOutSource("used"); setPreview(null); setAgreedValue(""); setOpDate(""); setInboundCondition("used"); } };

  const priceInvalid = !preview || preview.requires_evaluation || preview.price == null;
  const tradeInValue = priceInvalid ? null : preview.price;
  const recommended = tradeInValue;
  const cashDisc = preview?.cash_discount ?? 0;
  const agreedNum = agreedValue === "" ? recommended : Number(agreedValue || 0);
  const cashAgreed = agreedNum != null ? Math.max(0, Math.round(agreedNum - cashDisc)) : null;
  const negotiated = agreedNum != null && recommended != null && Number(agreedNum) !== Number(recommended);
  const marketPrice = priceFor(outbound);
  const diff = (agreedNum != null && marketPrice != null) ? Math.round(marketPrice - agreedNum) : null;

  const confirm = async () => {
    if (!outId) { toast.error("Selecciona el equipo DISPONIBLE que SALE."); return; }
    if (priceInvalid) { toast.error("El equipo que entra requiere evaluación técnica; no se puede cotizar."); return; }
    if (form.imei && !/^\d{15}$/.test(form.imei)) { toast.error("IMEI debe tener 15 dígitos"); return; }
    setSaving(true);
    try {
      const body = {
        model: form.model, capacity: form.capacity, color: form.color || null,
        battery_percent: Number(form.battery_percent),
        charge_cycles: form.charge_cycles === "" ? null : Number(form.charge_cycles),
        aesthetic: form.condition, problems: [form.damage || "none"], has_box: form.has_box === "si",
        sim_type: form.sim_type || null, imei: form.imei || null, outbound_inventory_id: outId,
        outbound_sale_price: marketPrice == null ? null : Number(marketPrice),
        outbound_kind: outSource,
        inbound_condition: inboundCondition,
        agreed_value: agreedValue === "" ? null : Number(agreedValue),
        operation_date: isAdmin && opDate ? opDate : null,
        withdrawal_id: prefill?.withdrawalId || null,
        customer: form.customer,
      };
      const { data } = await adminApi.post("/admin/manual-trade-in", body);
      toast.success(`Trade-In registrado ${data.id} · Contrato ${data.contract_number || "—"}`);
      setResult(data);
      setConfirming(false);
      onCreated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo registrar el Trade-In"); } finally { setSaving(false); }
  };

  const act = async (key, fmt, print) => { setBusy(key); try { await openContractWindow(result.contract_id, fmt, print); } finally { setBusy(""); } };
  const actW = async (key, fmt, print) => { setBusy(key); try { await openWarrantyWindow(result.warranty_id, fmt, print); } finally { setBusy(""); } };
  const lbl = (t) => <span className="text-[10px] text-zinc-400 px-1">{t}</span>;

  // Búsqueda + filtro de generación del equipo que SALE
  const term = q.trim().toLowerCase();
  const outResults = outActive
    .filter((it) => filterGen === "all" || (filterGen === "otros" ? ![17, 16, 15, 14, 13].includes(genOf(it.model)) : String(genOf(it.model)) === filterGen))
    .filter((it) => !term || [it.model, it.capacity, it.color, it.sim_type, it.id, it.imei].some((f) => f && String(f).toLowerCase().includes(term)))
    .sort((a, b) => genOf(b.model) - genOf(a.model) || String(a.model).localeCompare(String(b.model)))
    .slice(0, 40);

  const Progress = () => {
    const steps = [["1", "Equipo que entra"], ["2", "Equipo que sale"], ["3", "Cliente y resumen"]];
    return (
      <div className="flex items-center gap-2" data-testid="mti-progress">
        {steps.map(([n, label], i) => {
          const num = i + 1; const done = step > num; const active = step === num;
          return (
            <div key={n} className="flex items-center gap-2 min-w-0">
              <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${done ? "bg-emerald-600 text-white" : active ? "bg-zinc-900 text-white" : "bg-zinc-200 text-zinc-500"}`}>{done ? "✓" : n}</span>
              <span className={`text-xs font-medium truncate hidden sm:block ${active || done ? "text-zinc-900" : "text-zinc-400"}`}>{label}</span>
              {i < 2 && <span className="h-px w-4 sm:w-6 bg-zinc-200 shrink-0" />}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manual-tradein-form">
        <DialogHeader>
          <DialogTitle>Registrar Trade-In</DialogTitle>
          <DialogDescription>Flujo de mostrador en 3 pasos. El equipo que entra usa el motor de precios actual; el que sale usa el Mercado Alto de Venta Simple. El contrato se genera automáticamente.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3" data-testid="manual-tradein-success">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800 flex gap-2">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <div>
                <p className="font-semibold">Trade-In registrado · {result.id}</p>
                <p className="text-xs mt-1">Valor Trade-In: <b>{money(result.price)}</b> · Equipo: {result.model} {result.capacity || ""}</p>
                <p className="text-xs">📥 Entró: <b data-testid="manual-tradein-inbound">{result.inbound_inventory_id || "—"}</b> · 📤 Salió: <b data-testid="manual-tradein-outbound">{result.outbound_inventory_id || "—"}</b></p>
                <p className="text-xs">Contrato: <b data-testid="manual-tradein-contract">{result.contract_number || "—"}</b>{result.warranty_number ? <> · 🛡️ Garantía: <b data-testid="manual-tradein-warranty">{result.warranty_number}</b></> : ""}</p>
              </div>
            </div>
            {result.contract_id && (
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="manual-tradein-view-contract" variant="outline" onClick={() => act("view", "letter", false)} disabled={!!busy} className="h-11 rounded-xl">
                  {busy === "view" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><FileText className="w-4 h-4 mr-2" /> Ver contrato</>}
                </Button>
                <Button data-testid="manual-tradein-print-contract" onClick={() => act("print", "thermal", true)} disabled={!!busy} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800">
                  {busy === "print" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Printer className="w-4 h-4 mr-2" /> Imprimir contrato</>}
                </Button>
              </div>
            )}
            {result.warranty_id && (
              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="manual-tradein-view-warranty" variant="outline" onClick={() => actW("wview", "letter", false)} disabled={!!busy} className="h-11 rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  {busy === "wview" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-2" /> Ver garantía</>}
                </Button>
                <Button data-testid="manual-tradein-print-warranty" onClick={() => actW("wprint", "thermal", true)} disabled={!!busy} className="h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700">
                  {busy === "wprint" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Printer className="w-4 h-4 mr-2" /> Imprimir garantía</>}
                </Button>
              </div>
            )}
            <Button variant="ghost" onClick={() => close(false)} className="w-full h-10 rounded-xl" data-testid="manual-tradein-success-close">Cerrar</Button>
          </div>
        ) : !catalog ? (
          <p className="text-sm text-zinc-400 py-6 text-center">Cargando catálogo…</p>
        ) : confirming ? (
          /* ----- CONFIRMACIÓN ----- */
          <div className="space-y-4" data-testid="mti-confirm-panel">
            <p className="text-base font-semibold text-zinc-900">¿Confirmar este Trade-In?</p>
            <div className="rounded-2xl border border-zinc-200 divide-y divide-zinc-100 text-sm">
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">Equipo que entra</span><span className="font-medium text-right">{form.model} {form.capacity}</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">Entra a inventario</span><span className="font-medium text-right" data-testid="mti-confirm-inbound-dest">{inboundCondition === "new" ? "📱 iPhone Nuevos (Nuevo/Sellado)" : "📦 Usados"}</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">Valor Trade-In</span><span className="font-semibold text-emerald-700">{money(tradeInValue)}</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">Equipo que sale</span><span className="font-medium text-right">{outbound?.model} {outbound?.capacity}</span></div>
              <div className="flex justify-between px-4 py-2.5"><span className="text-zinc-500">Precio de mercado</span><span className="font-semibold">{money(marketPrice)}</span></div>
              <div className="flex justify-between px-4 py-2.5 bg-zinc-50"><span className="font-semibold text-zinc-900">{diff != null && diff < 0 ? "A favor del cliente" : "Diferencia a pagar"}</span><span className="font-bold text-lg text-zinc-900" data-testid="mti-confirm-diff">{money(diff == null ? null : Math.abs(diff))}</span></div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setConfirming(false)} disabled={saving} className="h-12 rounded-xl flex-1" data-testid="mti-confirm-cancel">Cancelar</Button>
              <Button onClick={confirm} disabled={saving} className="h-12 rounded-xl flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="mti-confirm-yes">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirmar"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {prefill?.withdrawalId && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-800" data-testid="mti-linked-withdrawal">
                <p className="font-semibold">Vinculado al retiro {prefill.withdrawalId}</p>
                <p className="mt-0.5">El equipo del consignador (que se lleva el cliente) ya está preseleccionado. Verifica y completa los datos del equipo que entrega el cliente — las notas del retiro son solo referencia, no se dan por verificadas.</p>
                {prefill.withdrawalNotes ? <p className="mt-1 text-indigo-600">Nota del retiro: {prefill.withdrawalNotes}</p> : null}
              </div>
            )}
            <Progress />

            {/* ============ PASO 1 — EQUIPO QUE ENTRA ============ */}
            {step === 1 && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="mti-step1">
                <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1"><ArrowDownLeft className="w-4 h-4" /> EQUIPO QUE ENTRA (lo entrega el cliente)</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <label className="flex flex-col gap-0.5">{lbl("📱 Modelo *")}
                    <ModelCombobox testid="mti-model" value={form.model} models={catalog.models} onChange={(name) => setForm((f) => ({ ...f, model: name, capacity: "", color: "" }))} /></label>
                  <label className="flex flex-col gap-0.5">{lbl("💾 Capacidad *")}
                    <select data-testid="mti-capacity" value={form.capacity} disabled={!selModel} onChange={(e) => set("capacity", e.target.value)} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm disabled:opacity-50">
                      <option value="">{selModel ? "Capacidad" : "Elige modelo"}</option>
                      {(selModel?.capacities || []).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select></label>
                  <label className="flex flex-col gap-0.5">{lbl("🎨 Color")}
                    <select data-testid="mti-color" value={form.color} disabled={!selModel || !form.capacity} onChange={(e) => set("color", e.target.value)} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm disabled:opacity-50">
                      <option value="">{form.capacity ? "Color" : "Elige capacidad"}</option>
                      {(selModel?.colors || []).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                    </select></label>
                  <label className="flex flex-col gap-0.5">{lbl("🔋 Batería % *")}
                    <Input data-testid="mti-battery" type="number" min="0" max="100" placeholder="0–100" value={form.battery_percent} onChange={(e) => set("battery_percent", e.target.value)} className="h-10 rounded-xl" /></label>
                  <label className="flex flex-col gap-0.5">{lbl(needsCycles ? "🔄 Ciclos (obligatorio)" : "🔄 Ciclos")}
                    <Input data-testid="mti-cycles" type="number" min="0" placeholder="Ciclos" value={form.charge_cycles} onChange={(e) => set("charge_cycles", e.target.value.replace(/[^0-9]/g, ""))} className="h-10 rounded-xl" /></label>
                  <label className="flex flex-col gap-0.5">{lbl("📋 Condición *")}
                    <select data-testid="mti-condition" value={form.condition} onChange={(e) => set("condition", e.target.value)} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm">
                      <option value="">Condición</option>
                      {catalog.aesthetic_conditions.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                    </select></label>
                  <label className="flex flex-col gap-0.5">{lbl("⚠️ Daños")}
                    <select data-testid="mti-damage" value={form.damage} onChange={(e) => set("damage", e.target.value)} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm">
                      {catalog.damages.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                    </select></label>
                  <label className="flex flex-col gap-0.5">{lbl("📦 Caja")}
                    <select data-testid="mti-has_box" value={form.has_box} onChange={(e) => set("has_box", e.target.value)} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm">
                      <option value="no">Sin caja</option>
                      <option value="si">Con caja original</option>
                    </select></label>
                  <label className="flex flex-col gap-0.5">{lbl("📶 Tipo de SIM")}
                    <select data-testid="mti-sim_type" value={form.sim_type} onChange={(e) => set("sim_type", e.target.value)} className="h-10 rounded-xl border border-zinc-200 px-3 text-sm">
                      <option value="">Tipo de SIM</option>
                      {(catalog.sim_types || []).map((s) => <option key={s.key} value={s.label}>{s.label}</option>)}
                    </select></label>
                  <label className="flex flex-col gap-0.5">{lbl("🔢 IMEI (15 dígitos)")}
                    <Input data-testid="mti-imei" inputMode="numeric" maxLength={15} placeholder="IMEI" value={form.imei} onChange={(e) => set("imei", e.target.value.replace(/\D/g, ""))} className="h-10 rounded-xl" /></label>
                </div>
                <div className="rounded-xl bg-white border border-emerald-200 px-3 py-2 text-sm" data-testid="mti-price-preview">
                  {!canPreview ? <span className="text-zinc-400">Completa modelo, capacidad, batería y condición para calcular el precio.</span>
                    : preview == null ? <span className="text-zinc-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Calculando…</span>
                    : preview.requires_evaluation || preview.price == null ? <span className="text-amber-600 font-medium">Requiere evaluación técnica — no se puede completar Trade-In automáticamente.</span>
                    : <span className="text-zinc-700">💰 Valor de Trade-In: <b className="text-emerald-700 text-base" data-testid="mti-price-value">{money(preview.price)}</b></span>}
                </div>
                {/* Condición del equipo recibido: Usado (default) | Nuevo/Sellado -> iPhone Nuevos */}
                <div className="rounded-xl bg-white border border-emerald-200 px-3 py-2.5 space-y-2" data-testid="mti-inbound-condition">
                  <p className="text-[11px] font-semibold text-emerald-700">Condición del equipo recibido</p>
                  <div className="flex rounded-full bg-zinc-100 p-1 w-full sm:w-fit">
                    <button type="button" data-testid="mti-inbound-used" onClick={() => setInboundCondition("used")}
                      className={`flex-1 sm:flex-none h-8 px-5 rounded-full text-xs font-semibold transition-colors ${inboundCondition === "used" ? "bg-zinc-900 text-white" : "text-zinc-500"}`}>Usado</button>
                    <button type="button" data-testid="mti-inbound-new" onClick={() => setInboundCondition("new")}
                      className={`flex-1 sm:flex-none h-8 px-5 rounded-full text-xs font-semibold transition-colors ${inboundCondition === "new" ? "bg-emerald-600 text-white" : "text-zinc-500"}`}>Nuevo / Sellado</button>
                  </div>
                  <p className="text-[11px] text-zinc-500">{inboundCondition === "new" ? "Ingresará directamente a Inventario iPhone Nuevos (precio de venta en $0, a definir por Admin)." : "Ingresará a Inventario Usados (flujo actual)."}</p>
                </div>
              </div>
            )}

            {/* ============ PASO 2 — EQUIPO QUE SALE ============ */}
            {step === 2 && (
              <div className="rounded-2xl border border-red-200 bg-red-50/30 p-3 space-y-3" data-testid="mti-step2">
                <p className="text-xs font-semibold text-red-700 flex items-center gap-1"><ArrowUpRight className="w-4 h-4" /> EQUIPO QUE SALE (se lo lleva el cliente)</p>
                <div className="flex rounded-full bg-white border border-zinc-200 p-1 w-full sm:w-fit" data-testid="mti-source-toggle">
                  <button data-testid="mti-source-used" onClick={() => { setOutSource("used"); setOutId(""); }} className={`flex-1 sm:flex-none h-8 px-5 rounded-full text-xs font-semibold transition-colors ${outSource === "used" ? "bg-zinc-900 text-white" : "text-zinc-500"}`}>Usado</button>
                  <button data-testid="mti-source-new" onClick={() => { setOutSource("new"); setOutId(""); }} className={`flex-1 sm:flex-none h-8 px-5 rounded-full text-xs font-semibold transition-colors ${outSource === "new" ? "bg-zinc-900 text-white" : "text-zinc-500"}`}>Nuevo</button>
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input data-testid="mti-outbound-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar equipo por modelo, IMEI o ID..." className="h-11 rounded-xl pl-9 bg-white" />
                </div>
                <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1" data-testid="mti-gen-filters">
                  {[["all", "Todos"], ["17", "17"], ["16", "16"], ["15", "15"], ["14", "14"], ["13", "13"], ["otros", "Otros"]].map(([v, label]) => (
                    <button key={v} data-testid={`mti-gen-${v}`} onClick={() => setFilterGen(v)}
                      className={`shrink-0 h-8 px-3 rounded-full text-xs font-medium transition-colors ${filterGen === v ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{label}</button>
                  ))}
                </div>

                {outbound && (
                  <div className="rounded-2xl border-2 border-zinc-900 bg-white p-3 flex items-center gap-3" data-testid="mti-outbound-detail">
                    <PhoneThumb model={outbound.model} color={outbound.color} className="h-12 w-12" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{outbound.model} <span className="font-normal text-zinc-400">{outbound.capacity}</span></p>
                      <p className="text-xs text-zinc-500 truncate">{[outbound.color, outbound.sim_type, imeiShort(outbound.imei), outbound.id].filter(Boolean).join(" · ")}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] text-zinc-400 uppercase tracking-wide">Precio de venta</p>
                      <p className="text-base font-bold text-zinc-900" data-testid="mti-outbound-price">{money(priceFor(outbound))}</p>
                    </div>
                  </div>
                )}

                {outActive.length === 0 ? <p className="text-xs text-amber-600">No hay equipos {outSource === "new" ? "NUEVOS" : "usados"} DISPONIBLES.</p>
                  : outResults.length === 0 ? <p className="text-sm text-zinc-400 py-6 text-center" data-testid="mti-outbound-empty">Sin resultados.</p>
                  : (
                  <div className="rounded-2xl border border-zinc-200 divide-y divide-zinc-100 overflow-hidden bg-white max-h-72 overflow-y-auto" data-testid="mti-outbound-list">
                    {outResults.map((it) => (
                      <button key={it.id} data-testid={`mti-outbound-${it.id}`} onClick={() => setOutId(it.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-zinc-50 ${outId === it.id ? "bg-zinc-100" : ""}`}>
                        <PhoneThumb model={it.model} color={it.color} className="h-10 w-10" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-zinc-900 truncate">{it.model} <span className="font-normal text-zinc-400">{it.capacity}</span></p>
                          <p className="text-xs text-zinc-500 truncate">{[it.color, it.sim_type, imeiShort(it.imei), it.id].filter(Boolean).join(" · ")}</p>
                        </div>
                        <span className="text-sm font-semibold text-zinc-900 shrink-0">{money(priceFor(it))}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ============ PASO 3 — CLIENTE Y RESUMEN ============ */}
            {step === 3 && (
              <div className="space-y-3" data-testid="mti-step3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-3">
                    <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold flex items-center gap-1"><ArrowDownLeft className="w-3.5 h-3.5" /> Entrega el cliente</p>
                    <p className="text-sm font-medium text-zinc-800 mt-1">{form.model} {form.capacity}</p>
                    <p className="text-xs text-zinc-500">Oferta recomendada</p>
                    <p className="text-xl font-bold text-emerald-700" data-testid="mti-summary-tradein">{money(tradeInValue)}</p>
                  </div>
                  <div className="rounded-2xl border border-zinc-200 bg-white p-3">
                    <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold flex items-center gap-1"><ArrowUpRight className="w-3.5 h-3.5" /> Se lleva el cliente</p>
                    <p className="text-sm font-medium text-zinc-800 mt-1">{outbound?.model} {outbound?.capacity}</p>
                    <p className="text-xs text-zinc-500">Precio de mercado</p>
                    <p className="text-xl font-bold text-zinc-900" data-testid="mti-summary-market">{money(marketPrice)}</p>
                  </div>
                </div>
                <div className="rounded-2xl border-2 border-zinc-900 bg-zinc-900 text-white p-4 flex items-center justify-between">
                  <span className="text-sm font-semibold">{diff != null && diff < 0 ? "💰 A favor del cliente" : "💰 Diferencia a pagar"}</span>
                  <span className="text-2xl font-bold" data-testid="mti-summary-diff">{money(diff == null ? null : Math.abs(diff))}</span>
                </div>

                {/* ===== VALOR ACORDADO (negociación real) ===== */}
                <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-3 space-y-2" data-testid="mti-agreed-block">
                  <div className="flex items-end justify-between gap-3">
                    <div className="flex-1">
                      <p className="text-[11px] uppercase tracking-wide text-amber-700 font-semibold">Valor acordado</p>
                      <Input data-testid="mti-agreed-value" type="number" inputMode="decimal" value={agreedValue} onChange={(e) => setAgreedValue(e.target.value)} placeholder={recommended != null ? String(recommended) : "$"} className="h-11 rounded-xl mt-1 bg-white text-lg font-bold" />
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[10px] text-zinc-500 uppercase">Efectivo equivalente</p>
                      <p className="text-lg font-bold text-zinc-900" data-testid="mti-cash-equivalent">{money(cashAgreed)}</p>
                      <p className="text-[10px] text-zinc-400">Trade {money(agreedNum)} · desc. {money(cashDisc)}</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-zinc-500">El sistema recomienda un valor, pero puedes ajustarlo según la negociación con el cliente.</p>
                  {negotiated && <p className="text-[11px] font-medium text-amber-700" data-testid="mti-negotiated-flag">⚖️ Negociación manual · recomendado {money(recommended)} → acordado {money(agreedNum)}</p>}
                  {isAdmin && (
                    <label className="flex flex-col gap-0.5 pt-1" data-testid="mti-opdate-wrap">
                      <span className="text-[10px] text-zinc-400 px-1">Fecha de la operación (Admin · opcional, para registrar una operación anterior)</span>
                      <Input data-testid="mti-operation-date" type="date" value={opDate} onChange={(e) => setOpDate(e.target.value)} className="h-10 rounded-xl bg-white" />
                    </label>
                  )}
                </div>

                <div className="border-t border-zinc-100 pt-3">
                  <p className="text-xs font-semibold text-zinc-500 mb-2">Datos del cliente</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input data-testid="mti-customer-name" placeholder="Nombre completo" value={form.customer.name} onChange={(e) => setC("name", e.target.value)} className="h-10 rounded-xl" />
                    <Input data-testid="mti-customer-phone" placeholder="Teléfono" value={form.customer.phone} onChange={(e) => setC("phone", e.target.value)} className="h-10 rounded-xl" />
                    <Input data-testid="mti-customer-doc" placeholder="Documento/ID (opcional)" value={form.customer.doc} onChange={(e) => setC("doc", e.target.value)} className="h-10 rounded-xl" />
                    <Input data-testid="mti-customer-email" placeholder="Correo (opcional)" value={form.customer.email} onChange={(e) => setC("email", e.target.value)} className="h-10 rounded-xl" />
                  </div>
                </div>
              </div>
            )}

            {/* ============ NAVEGACIÓN ============ */}
            <div className="flex gap-2 pt-1">
              {step === 1 ? (
                <>
                  <Button variant="outline" onClick={() => close(false)} className="h-12 rounded-xl flex-1" data-testid="mti-cancel">Cancelar</Button>
                  <Button onClick={() => setStep(2)} disabled={!canPreview || priceInvalid} className="h-12 rounded-xl flex-1 bg-zinc-900 hover:bg-zinc-800" data-testid="mti-next-1">Continuar <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </>
              ) : step === 2 ? (
                <>
                  <Button variant="outline" onClick={() => setStep(1)} className="h-12 rounded-xl flex-1" data-testid="mti-back-2"><ArrowLeft className="w-4 h-4 mr-1" /> Atrás</Button>
                  <Button onClick={() => setStep(3)} disabled={!outId} className="h-12 rounded-xl flex-1 bg-zinc-900 hover:bg-zinc-800" data-testid="mti-next-2">Continuar <ArrowRight className="w-4 h-4 ml-1" /></Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setStep(2)} className="h-12 rounded-xl flex-1" data-testid="mti-back-3"><ArrowLeft className="w-4 h-4 mr-1" /> Atrás</Button>
                  <Button onClick={() => setConfirming(true)} disabled={!outId || priceInvalid} className="h-12 rounded-xl flex-1 bg-emerald-600 hover:bg-emerald-700 font-semibold" data-testid="mti-confirm"><Handshake className="w-4 h-4 mr-2" /> Confirmar y Registrar Trade-In</Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const ManualTradeInTab = () => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const load = () => adminApi.get("/admin/quotes", { params: { trade_in_history: true } }).then((r) => setRows(r.data || [])).catch(() => toast.error("No se pudo cargar el historial"));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="manual-tradein-panel" className="space-y-4">
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-zinc-900 flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Trade-In manual en tienda</p>
          <p className="text-xs text-zinc-500 mt-1">Inicia un Trade-In completo desde la tienda: registra el equipo que entrega el cliente y el equipo DISPONIBLE que se lleva. Usa los precios y reglas actuales del sistema.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="h-11 rounded-xl bg-zinc-900 hover:bg-zinc-800 shrink-0" data-testid="manual-tradein-new-btn">+ Registrar Trade-In</Button>
      </div>

      {!rows ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div> : rows.length === 0 ? (
        <p className="text-sm text-zinc-400 py-8 text-center" data-testid="manual-tradein-empty">Aún no hay Trade-In manuales registrados.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-zinc-200">
          <table className="w-full text-xs" data-testid="manual-tradein-table">
            <thead className="bg-zinc-50 text-zinc-500 text-left"><tr>
              {["ID", "Entró (cliente)", "Precio", "Cliente", "Entró inv.", "Salió inv.", "Fecha", "Contrato"].map((h) => <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((q) => (
                <tr key={q.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`manual-tradein-row-${q.id}`}>
                  <td className="px-3 py-2 font-mono text-zinc-500">{q.id}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{q.model} {q.capacity || ""}{q.imei ? ` · ${q.imei}` : ""}</td>
                  <td className="px-3 py-2 font-semibold">{money(q.price)}</td>
                  <td className="px-3 py-2">{q.contact?.name || "—"}</td>
                  <td className="px-3 py-2 font-mono text-zinc-500">{q.trade_in_inventory_id || "—"}</td>
                  <td className="px-3 py-2 font-mono text-zinc-500">{q.trade_out_inventory_id || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{(q.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2"><ContractButton operationType="trade_in" operation={q} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ManualTradeInDialog open={open} onOpenChange={setOpen} onCreated={load} />
    </div>
  );
};
